/**
 * Calendar-date arithmetic on ISO strings ("2026-05-18").
 *
 * Every date in this app is a calendar day, never a moment in time. Working
 * on strings and UTC-based arithmetic means a week can never shift because
 * of a time zone or a daylight-saving change (CLAUDE.md §6, "Dates are the
 * hard part"). Nothing here reads the clock.
 */

/** A calendar date in ISO form, YYYY-MM-DD. */
export type ISODate = string;

/** Days of the week as JavaScript numbers them: 0 is Sunday, 1 is Monday. */
export const SUNDAY = 0;
export const MONDAY = 1;
export const TUESDAY = 2;
export const FRIDAY = 5;
export const SATURDAY = 6;

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Builds an ISO date from its parts (month is 1 to 12). */
export function isoDate(year: number, month: number, day: number): ISODate {
  const y = String(year).padStart(4, '0');
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** True when the string is a real calendar date in ISO form. */
export function isISODate(value: string): value is ISODate {
  const m = ISO_RE.exec(value);
  if (!m) return false;
  const [, y, mo, d] = m;
  const t = Date.UTC(Number(y), Number(mo) - 1, Number(d));
  return toISO(t) === value;
}

function toUTC(date: ISODate): number {
  const m = ISO_RE.exec(date);
  if (!m) throw new Error(`Not an ISO date: ${date}`);
  const [, y, mo, d] = m;
  return Date.UTC(Number(y), Number(mo) - 1, Number(d));
}

function toISO(utcMillis: number): ISODate {
  const dt = new Date(utcMillis);
  return isoDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/** The year of the date. */
export function yearOf(date: ISODate): number {
  return Number(date.slice(0, 4));
}

/** Adds (or, when negative, subtracts) whole days. */
export function addDays(date: ISODate, days: number): ISODate {
  return toISO(toUTC(date) + days * 86_400_000);
}

/**
 * Adds whole months, clamping the day to the end of the target month
 * (31 January + 1 month is 28 or 29 February).
 */
export function addMonths(date: ISODate, months: number): ISODate {
  const y = yearOf(date);
  const m = Number(date.slice(5, 7));
  const d = Number(date.slice(8, 10));
  const total = y * 12 + (m - 1) + months;
  const ty = Math.floor(total / 12);
  const tm = (total % 12) + 1;
  const lastDay = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  return isoDate(ty, tm, Math.min(d, lastDay));
}

/** Day of the week, 0 (Sunday) to 6 (Saturday). */
export function dayOfWeek(date: ISODate): number {
  return new Date(toUTC(date)).getUTCDay();
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from: ISODate, to: ISODate): number {
  return Math.round((toUTC(to) - toUTC(from)) / 86_400_000);
}

/** Lexical order of ISO dates is chronological order. */
export function compareDates(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** The Monday on or before the date (the date itself when it is a Monday). */
export function mondayOnOrBefore(date: ISODate): ISODate {
  const dow = dayOfWeek(date);
  const back = dow === SUNDAY ? 6 : dow - MONDAY;
  return addDays(date, -back);
}

/** The n-th given weekday of a month, e.g. the 3rd Monday of February. */
export function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): ISODate {
  const first = isoDate(year, month, 1);
  const offset = (weekday - dayOfWeek(first) + 7) % 7;
  return addDays(first, offset + (n - 1) * 7);
}

/** The last given weekday on or before the date. */
export function weekdayOnOrBefore(date: ISODate, weekday: number): ISODate {
  const back = (dayOfWeek(date) - weekday + 7) % 7;
  return addDays(date, -back);
}

/** Every Monday from the first Monday on or before `start` up to and including `end`. */
export function mondaysBetween(start: ISODate, end: ISODate): ISODate[] {
  const out: ISODate[] = [];
  for (let d = mondayOnOrBefore(start); compareDates(d, end) <= 0; d = addDays(d, 7)) out.push(d);
  return out;
}
